/**
 * /zh/business-roadmap — 业务路线图中文 SEO 页。与 BusinessRoadmap.tsx
 * 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function BusinessRoadmapZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/business-roadmap"
      documentTitle="业务路线图：定义、示例与制定方法 · Draftlize"
      documentDescription="业务路线图是什么、三个可参考的示例（初创、扩张、扭亏），以及六步制定方法。它与产品路线图、商业计划书的区别——以及当底层战略变动时，怎么让路线图跟着动而不是悄悄过时。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 业务路线图</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            业务路线图是什么？<br/>
            <em>把策略排好序、摆上台面。</em>
          </h1>
          <p className="lede">
            <em>业务路线图</em>（business roadmap）是一份高层计划，它把公司策略变成沿时间排列的一串举措、目标和里程碑——是「这是我们的策略」和「这是今年每个团队都在朝之努力的东西」之间的桥。产品路线图覆盖一个产品，业务路线图横跨整个公司：产品、GTM、招聘、运营、财务。这页讲业务路线图是什么、它和产品路线图、商业计划书有何不同、上面放什么——以及诚实的那部分：它压在会移动的策略赌注上，而静态的路线图不会跟着动。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>业务路线图是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            业务路线图是管理层向整个组织传达方向的方式：重大举措、它们服务的结果、以及大致何时，排好序，好让产品、销售、市场、运营朝同一个方向使劲。它运行在 <a href="/zh/what-is-a-product-roadmap" style={{ color: 'var(--amber)' }}>产品路线图</a> 之上一层——产品路线图通常是业务路线图<em>上的</em>一条流。<em>公司路线图</em>回答的是「整个业务想达成什么、按什么顺序、每一块归谁」。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            它是 <a href="/zh/product-strategy" style={{ color: 'var(--amber)' }}>策略</a> 的下游，也和商业<em>计划书</em>（给投资人或创始人、覆盖模式、市场、财务的详细文档）不同。路线图是策略的执行视图，计划书是其理据。业务里的「做路线图」，是随着公司不断学习、持续让那个执行视图保持诚实的行为。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">别搞混</span>
            <h2 className="serif">业务路线图 vs 产品路线图 <em>vs 商业计划书。</em></h2>
          </div>
          <p>三份都在讲「我们要去哪」的文档，处在不同高度、给不同读者。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>构件</th>
              <th>范围</th>
              <th>主要读者</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>业务路线图</td><td className="yes">整个公司，排好序</td><td>管理层与各团队</td></tr>
            <tr><td>产品路线图</td><td className="partial">一个产品的方向</td><td>产品与干系人</td></tr>
            <tr><td>商业计划书</td><td className="partial">模式、市场、财务</td><td>投资人与创始人</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">业务路线图上放什么。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">战略举措，按主题</h3>
            <p>那些重大赌注——「上探高端」「在 EMEA 上线」「实现盈利」——按主题分组，而不是一张任务清单。每个举措都连到一个战略目标，好让任何人都能看出它凭什么在路线图上。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">结果与里程碑，不只是日期</h3>
            <p>可衡量的目标（「ARR 到 500 万」「企业档上线」）配检查点。和产品路线图一样，对确定性要诚实——「now」做实、「later」做方向——别画业务没有的精确度。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">跨团队的负责人与依赖</h3>
            <p>每个举措归谁、依赖什么。这是公司路线图最难也最值钱的地方：那些没有任何单一团队的计划能覆盖的跨团队依赖（「GTM 上线得先有企业档」）。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">怎么制定一张业务路线图，<em>六步。</em></h2>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { t: '从策略出发，不从日历出发', d: '先写下公司今年真正在下的两三个战略赌注——路线图的存在就是为了给它们排序。赌注不显性，先补赌注。把各团队愿望清单收集起来的不叫路线图，叫排队。' },
            { t: '把每个赌注拆成有负责人的举措', d: '每个赌注拆成执行它的举措，每个举措恰好一个负责人。「上探高端」可能拆成企业档（产品）、大客户打法（销售）、合规认证（运营）。' },
            { t: '挂结果，不挂功能清单', d: '每个举措配一个可衡量的目标——「ARR 到 500 万」「企业档带 3 家设计伙伴上线」——让路线图能按结果被检验，而不是按忙碌程度。' },
            { t: '把跨团队依赖画出来', d: '这是公司路线图值得存在的一步：哪个举措等哪个。GTM 上线等企业档、EMEA 进入压在招聘计划上。把这些边显式写下来。' },
            { t: '按视界排序，对精确度诚实', d: 'Now / next / later 好过十二个月的假周级精确。近期条目给日期和负责人，远期条目给季度和理由。精确度匹配真实的确定性。' },
            { t: '发布前先定好更新机制', d: '没人回看的路线图是幻灯片，不是计划。发布前就约定：何时回看（每季度，外加任何核心赌注变化时）、谁负责把变化传导到每个被波及的团队。' },
          ].map((s, i) => (
            <div key={s.t} style={{ display: 'grid', gridTemplateColumns: '56px 1fr', gap: 16, padding: '16px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <span className="num" style={{ fontSize: 22 }}>{i + 1}</span>
              <div>
                <h3 className="serif" style={{ fontSize: 19, margin: '0 0 6px', color: 'var(--ink-0)' }}>{s.t}</h3>
                <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.8, color: 'var(--ink-1)' }}>{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">业务路线图<em>示例。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            三个不同阶段的公司速写。注意它们的共同点：主题少、结果显式、依赖写下来——以及共同没有的：任务级细节。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">初创——找到可重复的收入</h3>
            <p>全年就两个主题：验证一个客群（自助转化漏斗上线，Q2 前 20 个付费团队）、挣到下一轮的门票（客户案例，Q4 前 ARR 年化 100 万美元）。其余的——新平台、第二产品——全部显式放进非目标。初创的路线图靠「拒绝了什么」立身。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">扩张期——打开第二个市场</h3>
            <p>三个主题：进入 EMEA（主体、定价、Q3 前首批 10 个标杆客户）、企业就绪（Q2 前 SSO + SOC 2——EMEA 的单子压在它上面）、区域内运营（本地时区支持）。企业就绪和 EMEA 之间那条依赖边，是整张路线图上最重要的一笔。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">成熟期——扭亏冲盈利</h3>
            <p>主题：修毛利（重谈基础设施、Q2 前砍掉两个亏损 SKU）、聚焦产品线（三个重叠产品并成一条线）、Q4 实现盈亏平衡。里程碑是财务数字，负责人是高管，砍产品的决策连着它波及的每一份团队计划。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">业务路线图压在赌注上。<em>赌注会动。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            和每张路线图一样，业务路线图是覆盖在决策之上的视图——关于市场、时机、排序的战略赌注。当一个赌注变了，建立在它上面的举措和跨团队依赖本该知道。在一份幻灯片里，没有任何东西把它们连起来。Draftlize 持有底下的决策。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">举措连到它所服务的策略</h3>
            <p>在 Draftlize 里，每个举措是一张卡片，连着它所执行的战略赌注、以及它所依赖的举措。路线图不是一张静态幻灯片；它是一张图，其中「EMEA 上线」指向为它正名的决策、和它所等待的企业档工作。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">赌注一变，依赖它的标 stale</h3>
            <p>重排策略——推迟 EMEA、把盈利提前——所有压在旧顺序上的举措、里程碑、跨团队依赖自动标记 stale，就像构建系统让被改文件的下游全部失效一样。路线图因为赌注移动而重新规划，而不是因为某人重开了 deck。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">每个团队读到当前方向</h3>
            <p>产品、GTM、运营——以及帮着每一方的 AI agent——经 MCP 读到「活」的路线图，于是年中的策略转向抵达每个团队的计划，而不是留下五个团队执行着上季度方向的三个不同版本。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          业务路线图在第一天让整个公司对齐。麻烦在于它底下的策略一直在动，而那张幻灯片不动。
        </blockquote>
        <cite>留着路线图视图，把每个举措连到它所依赖的战略赌注。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '业务路线图和产品路线图有什么区别？', a: '业务路线图横跨整个公司——产品、GTM、招聘、运营——把战略举措跨团队排序；产品路线图覆盖单个产品的方向。产品路线图通常是更大的业务路线图上的一条流。' },
            { q: '业务路线图和商业计划书有什么区别？', a: '商业计划书是详细的理据——模式、市场、竞争、财务——常写给投资人；业务路线图是执行视图：哪些举措发生、按什么顺序、朝什么结果。计划书解释为什么，路线图给「做什么」排序。' },
            { q: '业务路线图该包含什么？', a: '按主题分组的战略举措、可衡量的结果与里程碑、每个举措的负责人、以及它们之间的跨团队依赖。和任何路线图一样，让日期的精确度匹配你真实的确定性。' },
            { q: '业务路线图该多久更新一次？', a: '它应当稳到能对齐团队——通常每季度、或当某个核心战略赌注变化时回看。纪律在于：当一个赌注移动时刻意地更新它，并确保每个团队的计划跟着动，而不是让它们悄悄分叉。' },
            { q: '业务路线图有什么示例？', a: '一家进入欧洲的扩张期公司，全年可能就三个主题：EMEA 进入（主体、定价、Q3 前 10 个标杆客户）、企业就绪（Q2 前 SSO 和 SOC 2，EMEA 的单子依赖它）、区域内支持。主题少、结果可衡量、有负责人、依赖写下来——这个形状在任何规模的公司都成立。' },
            { q: '初创公司怎么做业务路线图？', a: '控制在一页、最多两三个主题——通常是「验证客群」和「够到下一个融资里程碑」。每个主题挂一个可衡量的结果，把明确不做的列出来，并按月回看：早期赌注变得比成熟公司快，初创的路线图靠「拒绝了什么」和「更新多快」立身。' },
            { q: '什么是业务路线图管理（business roadmapping）？', a: '随着公司不断学习、持续让路线图为真的实践：从策略推导举措排序、画出跨团队依赖，以及最难的一步——当某个战略赌注移动时，把变化传导到每个团队的计划里，让所有人执行的是当前方向而不是上季度的。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让整个公司在一个方向上<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            留着你的路线图工具。在它底下加一层 Draftlize，让每个举措连到它所服务的战略赌注——这样年中策略转向时，每个团队的计划都会标出「刚刚变了什么」，而不是悄悄各自漂走。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
